import Joi from 'joi'
import { useEffect, useState } from 'react'
import { FiAlertCircle } from 'react-icons/fi'
import { useUiStore } from 'store'
import { GButton, GInput } from '../../../components/basic-blocks'
import { useStores } from '../../../hooks'
import { useAuth0 } from '@auth0/auth0-react'
import { useSearchParams } from 'react-router-dom'
import { NewOnboarding } from '../../../components/new-onboarding'

const emailSchema = Joi.object({
	email: Joi.string()
		.email({ tlds: { allow: false } })
		.required()
})
const validateEmail = (email: string) => {
	const { error } = emailSchema.validate({ email })
	return error ? error.details[0].message : null
}
function getEmail(email: string) {
	return <span className="text-t-email-blue font-normal">{email}</span>
}

const resetPasswordSteps = {
	reset_request: {
		title: 'Reset your password',
		sub_title: () =>
			'Forgot your password? No worries — enter your email and we’ll send you a link to reset it.'
	},
	email_sent: {
		title: 'Email sent',
		sub_title: (email: string) => (
			<>
				A link has been sent to your email {getEmail(email)}. Check your inbox
				for further instructions.
			</>
		)
	}
}
export const PasswordReset = () => {
	const { logout } = useAuth0()
	const [search] = useSearchParams()
	const queryEmail = search.get('email')
	const [step, setStep] = useState<'reset_request' | 'email_sent'>(
		'reset_request'
	)
	const {
		userStore: { passwordReset }
	} = useStores()
	const [loading, setLoading] = useState(false)
	const isLoading = useUiStore((s) => s.isLoading)
	const isErrored = useUiStore((s) => s.isErrored('USER_PASSWORD_RESET'))
	const errorMsg = useUiStore((s) => s.getError('USER_PASSWORD_RESET'))
	const [email, setEmail] = useState('')
	const [error, setError] = useState<string | undefined>()

	const handleSubmit = async () => {
		const validationError = validateEmail(email)
		if (validationError) return setError(validationError)
		setError(undefined)
		setLoading(true)
		const isSent = await passwordReset(email, true)
		isSent && setStep('email_sent')
		setLoading(false)
	}

	const resentEmail = async () => {
		await passwordReset(email, true)
	}
	useEffect(() => {
		if (!queryEmail) return
		if (validateEmail(queryEmail)) return // invalid email -> stop

		setEmail(queryEmail)
		setStep('email_sent')
		window.history.replaceState({}, '', `${window.location.pathname}`)
	}, [queryEmail])
	return (
		<div className="bg-t-smoke md:bg-gradient-to-br md:p-8  pt-12 px-4 min-h-screen from-g-purple via-g-cyan to-g-gray">
			<div className="absolute top-6 left-6 md:static md:mb-8">
				<img
					alt="logo"
					className="w-[156px] md:block hidden"
					src="https://storage.googleapis.com/clickguard-storage-dev/public/assets/img/Clickguard%20Logo%20New.svg"
				/>
			</div>
			<NewOnboarding
				backToLogin={step === 'reset_request'}
				title={resetPasswordSteps[step].title}
				description={resetPasswordSteps[step].sub_title(email)}
			>
				{step === 'reset_request' ? (
					<div className="w-full">
						<GInput
							type="email"
							autoComplete="email"
							label="Email"
							className="mb-2"
							value={email}
							inputClassName={
								' border-t-gray-input leading-4 font-[300] placeholder:text-t-gray-hold'
							}
							id="Email"
							placeholder="john@company.com"
							error={
								isErrored && errorMsg === 'User not found'
									? 'The email address you entered is not valid. Please try again.'
									: error
							}
							onChange={(e) => setEmail(e.target.value)}
							labelClassName="text-t-base-blue leading-4 font-normal mb-1.5"
							errorClassName="text-red-500 leading-4 text-md font-[300] "
							borderError={isErrored || !!error}
							errorIcon={FiAlertCircle}
						/>
						<div className="flex justify-center">
							<GButton
								color="yellow"
								labelClassName="font-base font-medium leading-5"
								className="w-full rounded-full p-3 mt-6 disabled:!shadow-none !shadow-s-yellow"
								iconPlacement="right"
								disabled={
									!email ||
									email?.length === 0 ||
									isLoading('USER_PASSWORD_RESET') ||
									loading
								}
								onClick={handleSubmit}
								loader={isLoading('USER_PASSWORD_RESET') || loading}
							>
								<span className="px-4">Reset password</span>
							</GButton>
						</div>
					</div>
				) : (
					<div className="w-full">
						<div className="flex justify-center">
							<GButton
								color="yellow"
								labelClassName="font-base font-medium leading-5"
								className="w-full rounded-full p-3 mt-6 disabled:!shadow-none !shadow-s-yellow"
								onClick={logout}
							>
								<span className="px-4">Back to Sign in</span>
							</GButton>
						</div>
						<div className="mt-4 flex gap-1 items-center justify-center cursor-pointer">
							<span className="text-t-base-blue text-base">
								Didn’t get the email?
							</span>
							<button
								onClick={resentEmail}
								className="text-t-blue-active text-md leading-5 font-normal flex items-center"
							>
								Resend email
							</button>
						</div>
					</div>
				)}
			</NewOnboarding>
		</div>
	)
}
